import { RootState } from '@iwProvider/configureStore';
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import {
    getOutstandingPrincipal,
    getMaxAmountCreditAvailable,
    getMaxOutstandingPrincipalBeforeTopUpAllowed,
    isTopUpEligible,
    amountToRepayBeforeTopUp,
    getDrawnDownLoanAmount,
    hasApprovalExpired,
    REVENUE_BASED_LOAN_PRODUCT_TYPE,
    isLongerLoan,
    isTopUpPending,
    hasAvailableCash
} from '../../multiLoanDashboard/accountsSelector';
import styles from './LoanProgressBar.module.css';
import { TProductType } from '../../dashboard/Dashboard.types';

type OtherProps = {
    productType: TProductType;
};

const mapStateToProps = (state: RootState, { productType }: OtherProps) => ({
    creditLimit:
        productType === REVENUE_BASED_LOAN_PRODUCT_TYPE
            ? getDrawnDownLoanAmount(state, REVENUE_BASED_LOAN_PRODUCT_TYPE)
            : getMaxAmountCreditAvailable(state, productType),
    currentPrincipal: getOutstandingPrincipal(state, productType),
    isTopUpEligible: isTopUpEligible(state, productType),
    isTopUpPending: isTopUpPending(state, productType),
    amountToRepayBeforeTopUp: amountToRepayBeforeTopUp(state, productType),
    loanAmount: getDrawnDownLoanAmount(state, productType),
    hasApprovalExpired: hasApprovalExpired(state, productType),
    hasAvailableCash: hasAvailableCash(state, productType),
    isLongerLoan: isLongerLoan(state),
    maxOutstandingPrincipalBeforeTopUpAllowed:
        getMaxOutstandingPrincipalBeforeTopUpAllowed(state, productType)
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;
type LoanProgressBarProps = PropsFromRedux & {
    productType: TProductType;
};

const LoanProgressBar = ({
    currentPrincipal,
    creditLimit,
    loanAmount,
    hasApprovalExpired,
    hasAvailableCash
}: LoanProgressBarProps) => {
    const [principalLoanAmountPercentage, setPrincipalLoanAmountPercentage] =
        useState(0);
    const isNewlyFundedLoan = currentPrincipal === creditLimit;
    const wasPartialDrawDown =
        creditLimit > loanAmount && hasApprovalExpired && !hasAvailableCash;
    const currentPrincipalPercent = (currentPrincipal / creditLimit) * 100;

    useEffect(() => {
        setPrincipalLoanAmountPercentage((loanAmount / creditLimit) * 100);
    }, [creditLimit, loanAmount]);

    const loanProgressBarClassName = classNames(
        styles.loanProgressBar,
        isNewlyFundedLoan && styles.newlyFundedLoan
    );

    return (
        <div data-testid="loan-progress-bar" className={styles.container}>
            {wasPartialDrawDown && (
                <div
                    className={styles.partialContainer}
                    data-testid="partial-drawdown-background"
                ></div>
            )}

            {wasPartialDrawDown && currentPrincipal < loanAmount && (
                <div
                    className={styles.drawDownLoanAmount}
                    data-testid="draw-down-loan-amount"
                    style={{
                        width: `${principalLoanAmountPercentage}%`
                    }}
                ></div>
            )}

            {currentPrincipal > 0 && (
                <>
                    <div
                        className={`${loanProgressBarClassName} ${
                            currentPrincipalPercent > 100
                                ? styles.loanProgressBarFull
                                : null
                        }`}
                        data-testid="current-principal-progress-bar"
                        style={{
                            width: `${
                                currentPrincipalPercent > 100
                                    ? 100
                                    : currentPrincipalPercent
                            }%`
                        }}
                    />
                </>
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(LoanProgressBar);
